import React from 'react';
import { TPA_EXPERIMENTS } from '@wix/pricing-plans-common/experiments';
import { useSettings } from '@wix/tpa-settings/react';
import { WidgetProps, useEnvironment, useExperiments } from '@wix/yoshi-flow-editor';
import loadable from '@wix/yoshi-flow-editor/loadable';
import { useSettingsParams } from '../../../hooks';
import { CommonProps, ListProps } from '../../../types/common';
import { PlanListSettingsAdapter } from '../../PlanList/PlanListSettingsAdapter';
import settingsParams, { MainViewDisplayType } from '../settingsParams';
import stylesParams from '../stylesParams';
import List from './List';
import { Modal } from './Modal';
import { WidgetContext } from './WidgetContext';

const LazyTabsView = loadable(() => import('./TabsView'));

type PlanListPageWidgetProps = CommonProps & ListProps;

const PlanListPageWidget: React.FC<WidgetProps<PlanListPageWidgetProps>> = (props) => {
  const { navigateToHomePage } = props;
  const { isMobile } = useEnvironment();
  const width = props.host.dimensions.width ?? (isMobile ? 320 : 980);

  const emptyState = React.useMemo(
    () => ({ isInWidget: false, onClick: () => navigateToHomePage(), subtitle: props.plansErrorMessage }),
    [navigateToHomePage, props.plansErrorMessage],
  );

  return (
    <WidgetContext
      settingsAdapterClass={PlanListSettingsAdapter}
      settingsParams={settingsParams}
      stylesParams={stylesParams}
      locale={props.locale}
      width={width}
    >
      <PlanListPageView emptyState={emptyState} {...props} />
      <Modal modal={props.modal} metaSiteId={props.metaSiteId} biUpgradeReferralClick={props.biUpgradeReferralClick} />
    </WidgetContext>
  );
};

type PlanListPageViewProps = WidgetProps<PlanListPageWidgetProps> & {
  emptyState: { isInWidget: boolean; onClick: () => void; subtitle?: string };
};

const PlanListPageView: React.FC<PlanListPageViewProps> = (props) => {
  const { experiments } = useExperiments();
  const settings = useSettings();
  const settingsParamsCtx = useSettingsParams();

  const isVariantsEnabled = experiments.enabled(TPA_EXPERIMENTS.ENABLE_VARIANTS);
  const shouldRenderTabsView =
    isVariantsEnabled && settings.get(settingsParamsCtx.mainViewDisplayType) === MainViewDisplayType.TABS;

  if (shouldRenderTabsView) {
    return <LazyTabsView {...props} />;
  }

  return <List {...props} />;
};

export default PlanListPageWidget;
